// apps/office/src/pages/office/PaymentsDashboard.jsx
import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DollarSign, Wallet, TrendingUp, AlertCircle, Plus, Search,
  Eye, Edit, Download, Mail, Trash2, Receipt, Loader2, FileText,
  CheckCircle, Bell, ChevronDown, ChevronRight, Printer, Users,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import ReceiptPDF from '../../components/office/ReceiptPDF';
import ReceivePaymentDialog from '../../components/office/ReceivePaymentDialog';
import EmailSendDialog from '../../components/office/EmailSendDialog';
import PaymentReminderDialog from '../../components/office/PaymentReminderDialog';

const rs = (v) => {
  const n = Number(v) || 0;
  return `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const STATUS_COLORS = {
  Completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Failed: 'bg-red-100 text-red-700 border-red-200',
  Refunded: 'bg-gray-100 text-gray-700 border-gray-200',
};

const statusToField = (status, due) => {
  if (due <= 0) return 'Paid';
  const map = {
    Draft: 'Draft',
    'Pending Approval': 'Pending Approval',
    Approved: 'Approved',
    Sent: 'Sent',
    Invoiced: 'Sent',
  };
  return map[status] || 'Approved';
};

const PaymentsDashboard = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('clients');
  const [search, setSearch] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContext, setDialogContext] = useState(null);
  const [emailPayment, setEmailPayment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [reminderGroup, setReminderGroup] = useState(null);

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);

  /* ── Quotations (approved/sent/invoiced) ── */
  const { data: quotations = [], isLoading: qLoading } = useQuery({
    queryKey: ['payments-quotations'],
    queryFn: () =>
      pb.collection('quotations').getFullList({
        sort: '-created',
        filter: 'isLatest = true && (status = "Approved" || status = "Invoiced" || status = "Sent")',
        expand: 'clientJobId',
      }),
  });

  /* ── Interior Quotations (approved/sent/invoiced) ── */
  const { data: interiorQuotations = [], isLoading: iqLoading } = useQuery({
    queryKey: ['payments-interior-quotations'],
    queryFn: async () => {
      const records = await pb.collection('interior_quotations').getFullList({
        sort: '-created',
        filter: 'isLatest = true && (status = "Approved" || status = "Invoiced" || status = "Sent")',
      });
      // Load project + client for each
      const projectIds = [...new Set(records.map((q) => q.projectId).filter(Boolean))];
      let projects = [];
      if (projectIds.length > 0) {
        projects = await pb.collection('interior_projects').getFullList({
          filter: projectIds.map((id) => `id = "${id}"`).join(' || '),
        });
      }
      const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
      const clientIds = [...new Set(projects.map((p) => p.clientId).filter(Boolean))];
      let clients = [];
      if (clientIds.length > 0) {
        clients = await pb.collection('leads').getFullList({
          filter: clientIds.map((id) => `id = "${id}"`).join(' || '),
        });
      }
      const clientsById = Object.fromEntries(clients.map((cl) => [cl.id, cl]));
      return records.map((q) => {
        const proj = projectsById[q.projectId] || null;
        const cl = proj ? (clientsById[proj.clientId] || null) : null;
        return {
          ...q,
          isInterior: true,
          expand: { clientJobId: cl },
          _project: proj,
        };
      });
    },
  });

  /* ── Payments ── */
  const { data: payments = [], isLoading: pLoading, refetch: refetchPayments } = useQuery({
    queryKey: ['paymentsDashboard'],
    queryFn: () =>
      pb.collection('payments').getFullList({
        sort: '-paymentDate',
        expand: 'leadId,quotationId',
      }),
  });

  /* ── Helpers ── */
  const paymentsForQuote = (quoteId, isInterior = false) =>
    payments.filter((p) => {
      if (isInterior) return p.interiorQuotationId === quoteId && (p.status || 'Completed') === 'Completed';
      return p.quotationId === quoteId && (p.status || 'Completed') === 'Completed';
    });

  const paidForQuote = (quoteId, isInterior = false) =>
    paymentsForQuote(quoteId, isInterior).reduce((s, p) => s + (Number(p.amount) || 0), 0);

  /* ── Client-wise grouping ── */
  const clientGroups = useMemo(() => {
    const map = {};

    /* Helper to add a quotation to map */
    const addQuote = (q, isInterior) => {
      const client = q.expand?.clientJobId || null;
      const clientId = client?.id || q.clientJobId || `orphan_${q.id}`;

      if (!map[clientId]) {
        map[clientId] = {
          clientId, client, quotations: [],
          totalQuote: 0, totalPaid: 0, totalDue: 0,
        };
      }

      const paid = paidForQuote(q.id, isInterior);
      const total = Number(q.total) || 0;
      const due = Math.max(0, total - paid);

      map[clientId].quotations.push({
        quotation: { ...q, isInterior, source: isInterior ? 'interior' : 'architecture' },
        total, paid, due,
        progress: total > 0 ? Math.min(100, (paid / total) * 100) : 0,
      });

      map[clientId].totalQuote += total;
      map[clientId].totalPaid += paid;
      map[clientId].totalDue += due;
    };

    /* Architecture quotations */
    quotations.forEach((q) => addQuote(q, false));
    /* Interior quotations */
    interiorQuotations.forEach((q) => addQuote(q, true));

    return Object.values(map)
      .map((g) => ({
        ...g,
        progress: g.totalQuote > 0 ? Math.min(100, (g.totalPaid / g.totalQuote) * 100) : 0,
      }))
      /* Filter out orphans: no client object AND no client name resolved */
      .filter((g) => {
        // Skip if client object missing AND clientId is orphan-* pattern
        if (g.client && (g.client.name || g.client.jobId)) return true;
        // Keep if clientId is a real lead id (not orphan)
        if (g.clientId && !String(g.clientId).startsWith('orphan_')) return true;
        return false;
      })
      .sort((a, b) => b.totalDue - a.totalDue); // highest due first
  }, [quotations, interiorQuotations, payments]);

  /* ── Global stats ── */
  const stats = useMemo(() => {
    const totalQuote = clientGroups.reduce((s, g) => s + g.totalQuote, 0);
    const totalPaid = clientGroups.reduce((s, g) => s + g.totalPaid, 0);
    const totalDue = clientGroups.reduce((s, g) => s + g.totalDue, 0);
    return { totalQuote, totalPaid, totalDue, clients: clientGroups.length };
  }, [clientGroups]);

  /* ── Filtering ── */
  const filteredGroups = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return clientGroups;
    return clientGroups.filter((g) =>
      g.client?.name?.toLowerCase().includes(s) ||
      g.client?.jobId?.toLowerCase().includes(s) ||
      g.quotations.some((q) => q.quotation.quoteNumber?.toLowerCase().includes(s))
    );
  }, [clientGroups, search]);

  const filteredPayments = useMemo(() => {
    /* Hide payments whose client (lead) was deleted AND interior/arch quotation gone */
    const valid = payments.filter((p) => {
      // If payment has no leadId AND no quotationId AND no interiorQuotationId → orphan
      const hasAny = p.leadId || p.quotationId || p.interiorQuotationId || p.client;
      return !!hasAny;
    });
    const s = search.toLowerCase();
    if (!s) return valid;
    return valid.filter((p) =>
      p.expand?.leadId?.name?.toLowerCase().includes(s) ||
      p.expand?.quotationId?.quoteNumber?.toLowerCase().includes(s) ||
      p.transactionId?.toLowerCase().includes(s) ||
      String(p.amount).includes(s)
    );
  }, [payments, search]);

  /* ── Handlers ── */
  const openReceivePayment = (quoteRow, group) => {
    setDialogContext({
      quotation: {
        ...quoteRow.quotation,
        source: quoteRow.quotation.source,
        isInterior: quoteRow.quotation.isInterior,
      },
      client: group.client,
      dueAmount: quoteRow.due,
      existingPayment: null,
    });
    setDialogOpen(true);
  };

  const openEditPayment = (payment) => {
    setDialogContext({
      quotation: payment.expand?.quotationId || null,
      client: payment.expand?.leadId || null,
      dueAmount: 0,
      existingPayment: payment,
    });
    setDialogOpen(true);
  };

  const deletePayment = async (payment) => {
    if (!window.confirm(`Delete this payment of ${rs(payment.amount)}?`)) return;
    setDeletingId(payment.id);
    try {
      await pb.collection('payments').delete(payment.id);
      toast.success('Payment deleted');
      queryClient.invalidateQueries(['paymentsDashboard']);
      refetchPayments();
    } catch (e) {
      toast.error('Delete failed: ' + e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const printReceipt = async (payment) => {
    try {
      const client = payment.expand?.leadId;
      const quotation = payment.expand?.quotationId;
      const quoteId = payment.quotationId || quotation?.id;
      const totalPaid = quoteId ? paidForQuote(quoteId) : Number(payment.amount) || 0;

      const blob = await pdf(
        <ReceiptPDF
          payment={payment}
          client={client}
          quotation={quotation}
          totalPaidForQuote={totalPaid}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch { window.open(url, '_blank'); }
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 60000);
      };
    } catch (e) {
      toast.error('Print failed: ' + e.message);
    }
  };

  const buildReceiptElement = (payment) => {
    const quoteId = payment.quotationId || payment.expand?.quotationId?.id;
    const totalPaid = quoteId ? paidForQuote(quoteId) : Number(payment.amount) || 0;
    return (
      <ReceiptPDF
        payment={payment}
        client={payment.expand?.leadId}
        quotation={payment.expand?.quotationId}
        totalPaidForQuote={totalPaid}
      />
    );
  };

  /* ── Reminder dialog data builder ── */
  const buildReminderInvoices = (group) =>
    group.quotations.map(({ quotation, total, due }) => ({
      id: quotation.id,
      invoiceNumber: quotation.quoteNumber,
      totalAmount: total,
      field: statusToField(quotation.status, due),
      invoiceDate: quotation.created,
      status: quotation.status,
    }));

  const clientPaymentsFor = (groupId) =>
    payments.filter(
      (p) => p.leadId === groupId || p.expand?.leadId?.id === groupId
    );

  if (qLoading || pLoading || iqLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-500 p-6 sm:p-8 shadow-xl">
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Wallet className="w-7 h-7" /> Payments
            </h1>
            <p className="text-white/80 text-sm mt-1">
              Client-wise payment tracking & receipts
            </p>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Quote Value</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{rs(stats.totalQuote)}</p>
                <p className="text-xs text-slate-400 mt-1">{stats.clients} clients</p>
              </div>
              <div className="bg-indigo-100 p-2.5 rounded-xl">
                <DollarSign className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{rs(stats.totalPaid)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {stats.totalQuote > 0 ? ((stats.totalPaid / stats.totalQuote) * 100).toFixed(1) : 0}% collected
                </p>
              </div>
              <div className="bg-emerald-100 p-2.5 rounded-xl">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total Due</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{rs(stats.totalDue)}</p>
                <p className="text-xs text-slate-400 mt-1">Pending collection</p>
              </div>
              <div className="bg-red-100 p-2.5 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Clients with Due</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">
                  {clientGroups.filter((g) => g.totalDue > 0).length}
                </p>
                <p className="text-xs text-slate-400 mt-1">Need follow-up</p>
              </div>
              <div className="bg-amber-100 p-2.5 rounded-xl">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by client, job ID, quote number, or reference…"
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="clients" className="rounded-lg gap-2 data-[state=active]:bg-white">
            <Users className="w-4 h-4" /> Clients ({clientGroups.length})
          </TabsTrigger>
          <TabsTrigger value="receipts" className="rounded-lg gap-2 data-[state=active]:bg-white">
            <Receipt className="w-4 h-4" /> Payment Receipts ({payments.length})
          </TabsTrigger>
        </TabsList>

        {/* ═══════ TAB 1: Clients ═══════ */}
        <TabsContent value="clients" className="mt-4 space-y-3">
          {filteredGroups.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No clients to collect payment from</p>
              </CardContent>
            </Card>
          ) : (
            filteredGroups.map((group) => {
              const isExpanded = expandedClientId === group.clientId;
              const hasDue = group.totalDue > 0;
              const fullyPaid = !hasDue && group.totalQuote > 0;

              return (
                <Card key={group.clientId} className="border-0 shadow-sm overflow-hidden">
                  {/* Header row — clickable */}
                  <div
                    className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50/60 transition-colors"
                    onClick={() => setExpandedClientId(isExpanded ? null : group.clientId)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Left: chevron + client info */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="mt-0.5 text-slate-400 shrink-0">
                          {isExpanded
                            ? <ChevronDown className="w-5 h-5" />
                            : <ChevronRight className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base sm:text-lg font-bold text-slate-800 truncate">
                              {group.client?.name || 'Unknown Client'}
                            </h3>
                            {fullyPaid && (
                              <Badge className="bg-emerald-100 text-emerald-700 gap-1 text-xs">
                                <CheckCircle className="w-3 h-3" /> Fully Paid
                              </Badge>
                            )}
                            {hasDue && (
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                Due
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">
                            {group.client?.jobId || '—'}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {group.quotations.length} quotation{group.quotations.length !== 1 ? 's' : ''} · {group.progress.toFixed(0)}% collected
                          </p>
                        </div>
                      </div>

                      {/* Right: 3-column totals + actions */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 shrink-0">
                        {/* Totals */}
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center sm:text-right">
                          <div>
                            <p className="text-[10px] uppercase text-slate-400 tracking-wide">Quote</p>
                            <p className="text-sm font-bold text-slate-700">{rs(group.totalQuote)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-slate-400 tracking-wide">Paid</p>
                            <p className="text-sm font-bold text-emerald-600">{rs(group.totalPaid)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-slate-400 tracking-wide">Due</p>
                            <p className={`text-sm font-bold ${hasDue ? 'text-red-600' : 'text-emerald-600'}`}>
                              {hasDue ? rs(group.totalDue) : 'Paid'}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div
                          className="flex gap-1.5 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {hasDue && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReminderGroup(group)}
                              className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5 text-xs"
                              title="Send Payment Reminder"
                            >
                              <Bell className="w-4 h-4" />
                              <span className="hidden sm:inline">Remind</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress bar (always visible) */}
                    <div className="mt-3 pl-8">
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${group.progress >= 100 ? 'bg-emerald-500' : 'bg-emerald-400'}`}
                          style={{ width: `${group.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expanded content — quotation sub-rows */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/40 p-3 sm:p-4">
                      <div className="space-y-2">
                        {group.quotations.map((row) => {
                          const { quotation, total, paid, due, progress } = row;
                          return (
                            <div
                              key={quotation.id}
                              className="bg-white rounded-lg border border-slate-100 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                            >
                              {/* Left: quote # + progress */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono font-semibold text-indigo-600 text-sm">
                                    {quotation.quoteNumber}
                                  </span>
                                  {due <= 0 && (
                                    <Badge className="bg-emerald-100 text-emerald-700 gap-1 text-[10px]">
                                      <CheckCircle className="w-3 h-3" /> Paid
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                  <span>{fmtDate(quotation.created)}</span>
                                  <span>·</span>
                                  <div className="flex-1 max-w-[120px]">
                                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-emerald-500"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                  <span>{progress.toFixed(0)}%</span>
                                </div>
                              </div>

                              {/* Right: amounts + receive button */}
                              <div className="flex items-center gap-3 sm:gap-5 shrink-0">
                                <div className="grid grid-cols-3 gap-3 sm:gap-5 text-right">
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-400">Quote</p>
                                    <p className="text-xs font-semibold text-slate-700">{rs(total)}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-400">Paid</p>
                                    <p className="text-xs font-semibold text-emerald-600">{rs(paid)}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase text-slate-400">Due</p>
                                    <p className={`text-xs font-bold ${due > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                      {due > 0 ? rs(due) : '—'}
                                    </p>
                                  </div>
                                </div>

                                {due > 0 ? (
                                  <Button
                                    size="sm"
                                    onClick={() => openReceivePayment(row, group)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                                  >
                                    <Plus className="w-4 h-4" /> Receive
                                  </Button>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                                    <CheckCircle className="w-3 h-3" /> Paid
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* ═══════ TAB 2: Payment Receipts ═══════ */}
        <TabsContent value="receipts" className="mt-4">
          <Card className="border-0 shadow-md overflow-hidden">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Receipt #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Quotation</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                        No payment receipts yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayments.map((payment) => {
                      const client = payment.expand?.leadId;
                      const quotation = payment.expand?.quotationId;
                      const receiptNo = payment.transactionId || `RCP-${payment.id.slice(-6).toUpperCase()}`;

                      return (
                        <TableRow key={payment.id} className="hover:bg-emerald-50/30">
                          <TableCell className="font-mono text-xs text-slate-500">{receiptNo}</TableCell>
                          <TableCell>
                            <p className="font-medium">{client?.name || '—'}</p>
                            <p className="text-xs text-slate-500">{client?.phone || ''}</p>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-indigo-600">
                            {quotation?.quoteNumber || '—'}
                          </TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">
                            {rs(payment.amount)}
                          </TableCell>
                          <TableCell>{fmtDate(payment.paymentDate)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-slate-50">
                              {payment.paymentMethod || '—'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[payment.status || 'Completed']}>
                              {payment.status || 'Completed'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button
                                variant="ghost" size="sm" title="View Receipt"
                                onClick={async () => {
                                  try {
                                    const blob = await pdf(buildReceiptElement(payment)).toBlob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  } catch (e) { toast.error('View failed: ' + e.message); }
                                }}
                                className="text-slate-600"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>

                              {isAdmin && (
                                <Button variant="ghost" size="sm" title="Edit Payment"
                                  onClick={() => openEditPayment(payment)}
                                  className="text-amber-600">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}

                              <PDFDownloadLink document={buildReceiptElement(payment)} fileName={`receipt_${receiptNo}.pdf`}>
                                {({ loading }) => (
                                  <Button variant="ghost" size="sm" disabled={loading} title="Download">
                                    <Download className="w-4 h-4" />
                                  </Button>
                                )}
                              </PDFDownloadLink>

                              <Button variant="ghost" size="sm" title="Print"
                                onClick={() => printReceipt(payment)}
                                className="text-slate-600">
                                <Printer className="w-4 h-4" />
                              </Button>

                              <Button variant="ghost" size="sm" title="Email Receipt"
                                onClick={() => setEmailPayment(payment)}
                                className="text-emerald-600">
                                <Mail className="w-4 h-4" />
                              </Button>

                              {isAdmin && (
                                <Button variant="ghost" size="sm" title="Delete"
                                  onClick={() => deletePayment(payment)}
                                  disabled={deletingId === payment.id}>
                                  {deletingId === payment.id
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <Trash2 className="w-4 h-4 text-red-500" />}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Receive / Edit Payment Dialog */}
      {dialogContext && (
        <ReceivePaymentDialog
          open={dialogOpen}
          onOpenChange={(v) => { setDialogOpen(v); if (!v) setDialogContext(null); }}
          quotation={dialogContext.quotation}
          client={dialogContext.client}
          dueAmount={dialogContext.dueAmount}
          existingPayment={dialogContext.existingPayment}
          onSuccess={() => {
            queryClient.invalidateQueries(['paymentsDashboard']);
            refetchPayments();
          }}
        />
      )}

      {/* Reminder Dialog (per client) */}
      {reminderGroup && (
        <PaymentReminderDialog
          open={!!reminderGroup}
          onOpenChange={(v) => !v && setReminderGroup(null)}
          client={reminderGroup.client}
          invoices={buildReminderInvoices(reminderGroup)}
          payments={clientPaymentsFor(reminderGroup.clientId)}
        />
      )}

      {/* Email Dialog (receipt) */}
      {emailPayment && (
        <EmailSendDialog
          open={!!emailPayment}
          onOpenChange={(v) => !v && setEmailPayment(null)}
          documentType="payment_receipt"
          document={{
            clientName: emailPayment.expand?.leadId?.name || '',
            clientEmail: emailPayment.expand?.leadId?.email || '',
            invoiceNumber: emailPayment.transactionId || `RCP-${emailPayment.id.slice(-6).toUpperCase()}`,
            clientJobIdText: emailPayment.expand?.leadId?.jobId || '',
          }}
          pdfElement={buildReceiptElement(emailPayment)}
        />
      )}
    </div>
  );
};

export default PaymentsDashboard;
